import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { Plus, Minus, ArrowLeft, Leaf, ShieldCheck, Truck, FlaskConical } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { ProductCard } from "@/components/ProductCard";
import { ProductReviews } from "@/components/ProductReviews";
import { Stars } from "@/components/StarRating";
import { Reveal, Stagger } from "@/lib/motion";
import { imgSrc } from "@/lib/img";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Gallery = ({ product }) => {
  const images = [product.image, ...(product.gallery || [])].filter((v, i, a) => v && a.indexOf(v) === i);
  const [active, setActive] = useState(0);
  useEffect(() => setActive(0), [product.id]);
  return (
    <div>
      <div className="aspect-square rounded-3xl overflow-hidden bg-creamdark border border-forest/10">
        <img data-testid="product-main-image" src={imgSrc(images[active] || product.image)} alt={product.name} className="w-full h-full object-cover" />
      </div>
      {images.length > 1 && (
        <div className="flex gap-3 mt-4">
          {images.map((src, i) => (
            <button key={src} data-testid={`product-thumb-${i}`} onClick={() => setActive(i)} className={`w-20 h-20 rounded-xl overflow-hidden border-2 transition-colors ${i === active ? "border-gold" : "border-transparent hover:border-forest/30"}`}>
              <img src={imgSrc(src)} alt="" className="w-full h-full object-cover" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const Section = ({ title, children, testId }) => (
  <Reveal>
    <div data-testid={testId} className="border-t border-forest/10 pt-8">
      <h2 className="eyebrow mb-4">{title}</h2>
      {children}
    </div>
  </Reveal>
);

export default function Product() {
  const { id } = useParams();
  const { add } = useCart();
  const [product, setProduct] = useState(null);
  const [related, setRelated] = useState([]);
  const [qty, setQty] = useState(1);
  const [variantId, setVariantId] = useState(null);
  const [missing, setMissing] = useState(false);

  useEffect(() => {
    setMissing(false);
    setQty(1);
    axios.get(`${API}/products/${id}`).then(({ data }) => {
      setProduct(data);
      const first = data.variants?.find((v) => v.stock === "in_stock") || data.variants?.[0];
      setVariantId(first ? first.id : null);
    }).catch(() => setMissing(true));
    axios.get(`${API}/products`).then(({ data }) => setRelated(data.filter((p) => p.id !== id).slice(0, 4))).catch(() => {});
  }, [id]);

  if (missing) {
    return (
      <div data-testid="product-not-found" className="container-nb py-32 text-center">
        <p className="font-serif text-3xl text-forest">We couldn't find that product.</p>
        <Link to="/collection" className="inline-flex items-center gap-2 mt-6 text-xs font-bold uppercase tracking-[0.18em] text-forest link-underline"><ArrowLeft size={13} /> Back to collection</Link>
      </div>
    );
  }
  if (!product) return <div className="min-h-[60vh]" />;

  const variant = product.variants?.find((v) => v.id === variantId) || null;
  const listPrice = variant ? variant.price : product.price;
  const finalPrice = variant ? variant.final_price : product.final_price;
  const available = product.stock === "in_stock" && (variant ? variant.stock === "in_stock" : true) && (product.variants?.length ? !!variant : true);

  return (
    <div data-testid="product-page" className="py-12 sm:py-20">
      <div className="container-nb">
        <Link to="/collection" data-testid="product-back-link" className="inline-flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.18em] text-mutedgreen hover:text-forest transition-colors"><ArrowLeft size={13} /> All Products</Link>

        <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 mt-8 items-start">
          <Reveal><Gallery product={product} /></Reveal>

          <Reveal delay={0.1}>
            <p className="text-[10px] uppercase tracking-[0.2em] text-gold font-bold">{product.collection}</p>
            <h1 data-testid="product-title" className="font-serif text-4xl sm:text-5xl text-forest mt-3 leading-[1.05]">{product.name}</h1>
            {product.rating_count > 0 && (
              <a href="#reviews" data-testid="product-rating-summary" className="inline-flex items-center gap-2 mt-3 group">
                <Stars value={product.rating_avg} size={15} />
                <span className="text-xs text-mutedgreen group-hover:text-forest">{product.rating_avg} · {product.rating_count} {product.rating_count === 1 ? "review" : "reviews"}</span>
              </a>
            )}
            <p className="font-serif italic text-mutedgreen text-lg mt-3">{product.tagline}</p>

            <div className="flex items-baseline gap-3 mt-6">
              <span data-testid="product-page-price" className="font-serif text-3xl text-forest font-semibold">₹{finalPrice}</span>
              {product.discount_label && <><s data-testid="product-page-mrp" className="text-mutedgreen">₹{listPrice}</s><span data-testid="product-page-discount" className="bg-gold text-white text-[10px] font-bold uppercase tracking-[0.15em] px-2.5 py-1 rounded-full">{product.discount_label}</span></>}
              <span className="text-xs text-mutedgreen">/ {variant ? variant.label : product.unit}</span>
            </div>
            {product.serving && <p className="text-xs text-mutedgreen mt-2">{product.serving}</p>}

            {product.variants?.length > 0 && (
              <div data-testid="product-variants" className="mt-6">
                <p className="text-[11px] font-bold uppercase tracking-[0.14em] text-forest/70 mb-2.5">Pack size</p>
                <div className="flex flex-wrap gap-2">
                  {product.variants.map((v) => {
                    const out = v.stock !== "in_stock";
                    const sel = v.id === variantId;
                    return (
                      <button key={v.id} data-testid={`variant-${v.id}`} onClick={() => setVariantId(v.id)} className={`relative px-4 py-2.5 rounded-full border text-xs font-semibold transition-colors ${sel ? "bg-forest text-cream border-forest" : "bg-white text-forest border-forest/20 hover:border-forest"} ${out ? "opacity-50 line-through" : ""}`}>
                        {v.label} <span className={`ml-1.5 ${sel ? "text-goldlight" : "text-mutedgreen"}`}>₹{v.final_price}</span>
                        {out && <span className="block text-[9px] no-underline tracking-wider uppercase">Out of stock</span>}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            <p className="text-sm sm:text-base text-ink/80 leading-relaxed mt-6">{product.description}</p>

            {product.benefits?.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-6">
                {product.benefits.map((b) => <span key={b} className="inline-flex items-center gap-1.5 bg-[#E8F0EA] text-forest text-[11px] font-semibold px-3 py-1.5 rounded-full"><Leaf size={11} className="text-gold" /> {b}</span>)}
              </div>
            )}

            <div className="flex flex-wrap items-center gap-4 mt-8">
              {available ? (
                <>
                  <div className="flex items-center border border-forest/15 rounded-full bg-white">
                    <button data-testid="product-qty-decrease" onClick={() => setQty((q) => Math.max(1, q - 1))} aria-label="Decrease" className="p-3 px-4 text-forest hover:text-gold"><Minus size={14} /></button>
                    <span data-testid="product-qty" className="font-semibold w-6 text-center">{qty}</span>
                    <button data-testid="product-qty-increase" onClick={() => setQty((q) => q + 1)} aria-label="Increase" className="p-3 px-4 text-forest hover:text-gold"><Plus size={14} /></button>
                  </div>
                  <button data-testid="product-add-to-cart" onClick={() => add(product.id, qty, true, variantId)} className="group inline-flex items-center gap-2.5 bg-forest text-cream text-xs font-semibold uppercase tracking-[0.18em] px-8 py-4 rounded-full hover:bg-gold transition-colors duration-300">
                    Add to Basket — ₹{(finalPrice * qty).toFixed(0)}
                  </button>
                </>
              ) : (
                <button disabled data-testid="product-add-to-cart" className="bg-creamdark text-forest/60 text-xs font-bold uppercase tracking-[0.15em] px-8 py-4 rounded-full cursor-not-allowed">{product.stock === "coming_soon" ? "Coming soon" : "Out of stock"}</button>
              )}
            </div>

            <div className="grid grid-cols-3 gap-3 mt-8">
              {[[FlaskConical, "Lab tested"], [ShieldCheck, "100% natural"], [Truck, "Free shipping"]].map(([Icon, t]) => (
                <div key={t} className="flex items-center gap-2 text-[11px] text-forest/80 font-medium"><Icon size={14} className="text-gold shrink-0" /> {t}</div>
              ))}
            </div>
          </Reveal>
        </div>

        <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 mt-16 sm:mt-24">
          <div className="space-y-10">
            {product.ingredients && <Section title="Ingredients" testId="product-ingredients"><p className="text-sm sm:text-base text-ink/80 leading-relaxed">{product.ingredients}</p></Section>}
            {product.how_to_use && <Section title="How to use" testId="product-how-to-use"><p className="text-sm sm:text-base text-ink/80 leading-relaxed whitespace-pre-line">{product.how_to_use}</p></Section>}
          </div>
          {product.nutrition?.length > 0 && (
            <Section title="Nutrition facts" testId="product-nutrition">
              <div className="bg-white border border-forest/10 rounded-2xl overflow-hidden">
                <div className="px-5 py-3 bg-creamdark/60 text-[11px] font-bold uppercase tracking-[0.14em] text-forest/70 flex justify-between"><span>Per serving{product.serving ? ` · ${product.serving.split("·")[0].trim()}` : ""}</span><span>Amount</span></div>
                {product.nutrition.map((row, i) => (
                  <div key={`${row.label}-${i}`} className="px-5 py-3 flex justify-between text-sm border-t border-forest/5"><span className="text-forest">{row.label}</span><span className="font-semibold text-forest">{row.value}</span></div>
                ))}
                <p className="px-5 py-3 text-[10px] text-mutedgreen border-t border-forest/5">Values are approximate and may vary naturally between harvests. This product is a food, not a medicine.</p>
              </div>
            </Section>
          )}
        </div>

        {related.length > 0 && (
          <div className="mt-20 sm:mt-28">
            <h2 className="font-serif text-2xl sm:text-3xl text-forest mb-8">You may also like</h2>
            <Stagger className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {related.map((p) => <ProductCard key={p.id} product={p} />)}
            </Stagger>
          </div>
        )}

        <div id="reviews" className="scroll-mt-24">
          <ProductReviews productId={product.id} productName={product.name} />
        </div>
      </div>
    </div>
  );
}
